import { z } from 'zod'

const COST_TYPES = [
  'preCarriage',
  'mainCarriage',
  'onCarriage',
  'customs',
  'insurance',
  'handlingFee',
  'voucher',
  'carbonOffset',
  'additionalCharges',
  'sea-freight',
  'air-freight',
  'rail-freight',
  'door-carriage-origin',
  'door-carriage-destination',
  'customs-import',
  'customs-export',
  'sustainability',
  'buyers-consolidation',
  'dangerous-goods',
  'special-equipment',
  'local-handling-origin',
  'local-handling-destination',
  'miscellaneous',
] as const

export type CostType = (typeof COST_TYPES)[number]

export type Currency = {
  value: number
  currency: string
}

export type PriceBreakdownLineItemResponse = {
  name: string
  code: string | null
  originalValue: Currency
  invoiceValue: Currency
}

export type PriceBreakdownCategoryResponse = {
  type: CostType
  lineItems: PriceBreakdownLineItemResponse[]
}

export type PriceBreakdownResponse = {
  referenceId: string
  categories: PriceBreakdownCategoryResponse[]
}

const LineItemSchema = z.object({
  name: z.string(),
  code: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  invoiceValue: z
    .object({
      value: z.number(),
      currency: z.string(),
    })
    .nullish()
    .transform((data) => data ?? { value: 0, currency: 'EUR' }),
  originalValue: z
    .object({
      value: z.number(),
      currency: z.string(),
    })
    .nullish()
    .transform((data) => data ?? { value: 0, currency: 'EUR' }),
})

export type LineItem = z.infer<typeof LineItemSchema>

const PriceBreakdownCategorySchema = z.object({
  type: z.enum(COST_TYPES),
  lineItems: z.array(LineItemSchema),
})

export const PriceBreakdownSchema = z.object({
  referenceId: z.string(),
  categories: z.array(PriceBreakdownCategorySchema),
  categoryCosts: z.record(
    z.enum(COST_TYPES),
    z.array(
      z.object({
        value: z.number(),
        currency: z.string(),
      })
    )
  ),
  totalCosts: z.array(
    z.object({
      value: z.number(),
      currency: z.string(),
    })
  ),
})

export type PriceBreakdownType = z.infer<typeof PriceBreakdownSchema>
